/**
 * 房源标签：字典编码常量 + 字典项读取。
 *
 * 标签不是固定枚举，而是由「房源配置 → 房源标签」维护的字典项：
 * 数据落在 park_dictionary（code=SPACE_TAG_DICTIONARY_CODE）与 park_dictionary_item，
 * 房源侧只在 park_space.tags 里存字典项的 value。
 *
 * 放在 SpaceManagement 下（而不是 SpaceSettings）是为了避免
 * SpaceSettings ↔ SpaceManagement 的循环依赖：SpaceForm 与房源台账都要读标签。
 */
import React from 'react';
import { parkApi } from '@/services/bff/park.api';
import type { Dictionary } from '@/services/bff/types/park';

export const SPACE_TAG_DICTIONARY_CODE = 'space_tag';

export const SPACE_TAG_DICTIONARY_NAME = '房源标签';

export const SPACE_TAG_DICTIONARY_DESCRIPTION =
  '用于给房源打标签，便于分类检索；房源档案中可按标签筛选。';

/** 取（或首次创建）房源标签字典，返回字典及其字典项 */
export const ensureSpaceTagDictionary = async (): Promise<Dictionary> =>
  parkApi.ensureDictionary({
    code: SPACE_TAG_DICTIONARY_CODE,
    name: SPACE_TAG_DICTIONARY_NAME,
    description: SPACE_TAG_DICTIONARY_DESCRIPTION,
  });

/**
 * 只读地取房源标签字典；尚未维护过时返回 null。
 *
 * 与 ensureSpaceTagDictionary 的区别：本方法不会创建字典。
 * 房源表单属于「读」路径，打开表单不应产生写操作，因此用它而不是 ensure。
 */
export const fetchSpaceTagDictionary = async (): Promise<Dictionary | null> => {
  const result = await parkApi.listDictionaries({
    keyword: SPACE_TAG_DICTIONARY_CODE,
    pageSize: 100,
  });
  const matched = (result.items || []).find(
    (item) => item.code === SPACE_TAG_DICTIONARY_CODE,
  );
  if (!matched) return null;
  return parkApi.getDictionary(matched.uuid);
};

/** 标签取值 -> 展示名称；字典里找不到时回退为原始取值 */
export const buildTagLabelMap = (
  items?: { value: string; label: string }[],
): Map<string, string> =>
  new Map((items || []).map((item) => [item.value, item.label]));

/**
 * 读取房源标签，供台账筛选、房源表单与详情页共用。
 *
 * 只读：不会创建字典（写路径只有「房源配置 → 房源标签」的 ensure）。
 * 读取失败时返回空列表，不阻断宿主页面。
 *
 * 刻意区分两份数据：
 * - `tagOptions` 只含**启用中**的标签，用于选择器与筛选项（停用的不该再被选）；
 * - `tagLabelMap` 含**全部**标签（含停用），用于展示。
 *   已停用标签的房源仍要显示它原本的名称，否则会退化成裸 value（如「metro」）。
 */
export const useSpaceTagOptions = (): {
  tagOptions: { value: string; label: string }[];
  tagLabelMap: Map<string, string>;
  reloadTags: () => Promise<void>;
} => {
  const [items, setItems] = React.useState<
    { value: string; label: string; active: boolean }[]
  >([]);

  const reloadTags = React.useCallback(async () => {
    try {
      const dictionary = await fetchSpaceTagDictionary();
      setItems(
        (dictionary?.items || []).map((item) => ({
          value: item.value,
          label: item.label,
          active: item.status === 'active',
        })),
      );
    } catch {
      setItems([]);
    }
  }, []);

  const tagOptions = React.useMemo(
    () =>
      items
        .filter((item) => item.active)
        .map((item) => ({ value: item.value, label: item.label })),
    [items],
  );

  const tagLabelMap = React.useMemo(() => buildTagLabelMap(items), [items]);

  return { tagOptions, tagLabelMap, reloadTags };
};
